import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Segmented, Skeleton } from "antd";
import {
  CheckCircleOutlined,
  HistoryOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { KINDS, KIND_ORDER, KindDot, KindTag } from "./KindTag";
import { EntityPair } from "./EntityPair";

export const WIDE_MIN = 1100;

/** true while the viewport is at least `min` px wide. */
export function useWide(min = WIDE_MIN) {
  const query = `(min-width: ${min}px)`;
  const get = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : true;
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on);
    else if (mq.addListener) mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else if (mq.removeListener) mq.removeListener(on);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  return wide;
}

/** The legend of the change kinds: icon + word + what it means (+ how many, when known). */
export function KindLegend({ counts }) {
  return (
    <div className="sv-legend">
      <div className="sv-legend-title">Comment lire cette page</div>
      <div className="sv-legend-grid">
        {KIND_ORDER.map(k => (
          <div key={k} className="sv-legend-item">
            <KindTag kind={k} />
            <span className="sv-legend-text">
              {KINDS[k].explain}
              {counts && counts[k] ? (
                <span className="sv-legend-count">
                  {" "}
                  · {Number(counts[k]).toLocaleString("fr-CH")}
                </span>
              ) : null}
            </span>
          </div>
        ))}
        <div className="sv-legend-item">
          <span className="sv-kind sv-kind--same">= identique</span>
          <span className="sv-legend-text">
            rien ne change — caché derrière « + N champs identiques »
          </span>
        </div>
      </div>
    </div>
  );
}

/** Sticky « Avant | Après » column headers (wide) or a two-pill key (stacked). */
export function ColumnHeads({ wide }) {
  return (
    <div className={`sv-colheads${wide ? " sv-colheads--sticky" : ""}`}>
      <div className="sv-colhead sv-colhead--before">
        <HistoryOutlined className="sv-colhead-icon" />
        <div>
          <div className="sv-colhead-title">Avant (aujourd'hui)</div>
          <div className="sv-colhead-sub">
            ce qui existe maintenant dans la base
          </div>
        </div>
      </div>
      <div className="sv-colhead sv-colhead--after">
        <CheckCircleOutlined className="sv-colhead-icon" />
        <div>
          <div className="sv-colhead-title">Après (une fois appliquée)</div>
          <div className="sv-colhead-sub">avec vos décisions</div>
        </div>
      </div>
    </div>
  );
}

/** Two skeleton cards shaped like a pair, while a section loads. */
export function PairSkeleton({ wide }) {
  return (
    <div className={`sv-skel${wide ? " sv-skel--wide" : ""}`}>
      <div className="sv-skel-card">
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
      <div className="sv-skel-card sv-skel-card--after">
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    </div>
  );
}

/**
 * One section: title with icon and counts, the « Ce qui se passe ici » sentence, the pairs,
 * « Afficher 30 de plus » for paginated record sections, « Rien ne change ici. » when empty.
 */
export function Section({
  def,
  state,
  navInfo,
  wide,
  target,
  intro,
  extra,
  targetBadge,
  onMore,
  onRetry,
  q,
}) {
  const Icon = def.icon;
  const items = (state && state.items) || [];
  const total = state ? state.total : 0;
  const loading = !state || state.loading;
  const changed = navInfo ? navInfo.changed : null;
  return (
    <section
      id={`sv-sec-${def.key}`}
      className="sv-section"
      data-section={def.key}
    >
      <div className="sv-section-head">
        <div className="sv-section-icon">
          <Icon />
        </div>
        <div className="sv-section-titles">
          <h3 className="sv-section-title">{def.label}</h3>
          <div className="sv-section-meta">
            {changed !== null && navInfo && (
              <span>
                {changed.toLocaleString("fr-CH")}{" "}
                {changed > 1 ? "changent" : "change"} sur{" "}
                {navInfo.total.toLocaleString("fr-CH")}{" "}
                {navInfo.total > 1 ? def.noun[1] : def.noun[0]}
              </span>
            )}
            {navInfo &&
              navInfo.kinds.map(k => <KindTag key={k} kind={k} size="small" />)}
          </div>
        </div>
      </div>
      <p className="sv-what">
        <strong>Ce qui se passe ici : </strong>
        {intro || def.what(target, navInfo ? navInfo.total : 0)}
      </p>
      {extra}
      {state && state.error ? (
        <div className="sv-error">
          Cette partie n'a pas pu être chargée : {state.error}{" "}
          <Button size="small" onClick={onRetry}>
            Réessayer
          </Button>
        </div>
      ) : loading && !items.length ? (
        <PairSkeleton wide={wide} />
      ) : !items.length ? (
        <div className="sv-nothing">
          {q
            ? `Aucune fiche ne correspond à « ${q} » ici.`
            : "Rien ne change ici."}
        </div>
      ) : (
        <div className="sv-pairs">
          {items.map(it => (
            <EntityPair
              key={it.key}
              item={it}
              stacked={!wide}
              compact={!!def.records}
              targetBadge={targetBadge ? targetBadge(it) : null}
            />
          ))}
        </div>
      )}
      {items.length > 0 && items.length < total && (
        <div className="sv-more">
          <Button onClick={onMore} loading={state.loadingMore} size="large">
            Afficher {Math.min(30, total - items.length)} de plus
          </Button>
          <span className="sv-more-count">
            {items.length.toLocaleString("fr-CH")} sur{" "}
            {total.toLocaleString("fr-CH")} affichées
          </span>
        </div>
      )}
    </section>
  );
}

/**
 * The frame of the final check: filter + search on top, a sticky section navigation on the
 * left (≥ 1100 px) with per-section counts and kind dots, the current section highlighted
 * while scrolling, and the sticky « Avant | Après » heads over the content.
 */
export function SplitLayout({
  wide,
  top,
  nav,
  filter,
  onFilter,
  q,
  onQ,
  children,
}) {
  const [active, setActive] = useState(nav[0] && nav[0].key);
  const contentRef = useRef(null);
  const navKeys = nav.map(n => n.key).join(",");

  useEffect(() => {
    if (!wide || typeof IntersectionObserver === "undefined") return undefined;
    const root = contentRef.current;
    if (!root) return undefined;
    const visible = new Map();
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e =>
          visible.set(e.target.getAttribute("data-section"), e.isIntersecting),
        );
        const first = nav.find(n => visible.get(n.key));
        if (first) setActive(first.key);
      },
      { rootMargin: "-140px 0px -55% 0px", threshold: 0 },
    );
    root.querySelectorAll("section[data-section]").forEach(s => io.observe(s));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wide, navKeys]);

  const go = key => {
    const el = document.getElementById(`sv-sec-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(key);
  };

  return (
    <div className={`sv-root${wide ? " sv-root--wide" : ""}`}>
      {top}
      <div className="sv-toolbar">
        <div className="sv-toolbar-group">
          <span className="sv-toolbar-label">Montrer :</span>
          <Segmented
            value={filter}
            onChange={onFilter}
            options={[
              { value: "changed", label: "Seulement ce qui change" },
              { value: "all", label: "Tout" },
            ]}
          />
        </div>
        <Input
          className="sv-search"
          allowClear
          size="middle"
          prefix={<SearchOutlined />}
          placeholder="Chercher une fiche (nom, n°, e-mail)"
          value={q}
          onChange={e => onQ(e.target.value)}
        />
      </div>
      <div className="sv-body">
        {wide && (
          <nav className="sv-nav" aria-label="Sections">
            <div className="sv-nav-title">Sections</div>
            {nav.map(n => (
              <button
                type="button"
                key={n.key}
                className={`sv-nav-item${active === n.key ? " is-active" : ""}`}
                onClick={() => go(n.key)}
              >
                <span className="sv-nav-text">
                  <span className="sv-nav-label">{n.label}</span>
                  {n.kinds.length > 0 && (
                    <span className="sv-nav-dots">
                      {n.kinds.map(k => (
                        <KindDot key={k} kind={k} />
                      ))}
                    </span>
                  )}
                </span>
                {n.changed !== undefined && (
                  <span
                    className={`sv-nav-count${n.changed ? "" : " is-zero"}`}
                    title="fiches qui changent"
                  >
                    {n.changed === null
                      ? "…"
                      : n.changed.toLocaleString("fr-CH")}
                  </span>
                )}
              </button>
            ))}
          </nav>
        )}
        <div className="sv-content" ref={contentRef}>
          <ColumnHeads wide={wide} />
          {children}
        </div>
      </div>
    </div>
  );
}
